import { Link } from "react-router-dom";

interface PathCardProps {
  to: string;
  eyebrow: string;
  title: string;
  blurb: string;
  accent: "leviathan" | "interceptor";
  cta: string;
  status?: string;
}

function PathCard({ to, eyebrow, title, blurb, accent, cta, status }: PathCardProps) {
  const accentRing =
    accent === "leviathan"
      ? "hover:border-leviathan-600/70 hover:shadow-leviathan-600/10"
      : "hover:border-interceptor-600/70 hover:shadow-interceptor-600/10";
  const accentText = accent === "leviathan" ? "text-leviathan-500" : "text-interceptor-500";

  return (
    <Link
      to={to}
      className={`group relative flex flex-col gap-3 rounded-xl border border-void-800 bg-void-900/60 p-7 shadow-lg shadow-black/20 transition-all duration-200 hover:-translate-y-0.5 ${accentRing}`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`font-display text-xs font-semibold tracking-[0.18em] uppercase ${accentText}`}
        >
          {eyebrow}
        </span>
        {status && (
          <span className="rounded-full border border-void-700 px-2.5 py-0.5 text-[10px] tracking-wide text-void-400 uppercase">
            {status}
          </span>
        )}
      </div>

      <h2 className="font-display text-2xl font-semibold text-void-200 transition-colors group-hover:text-white">
        {title}
      </h2>

      <p className="text-sm leading-relaxed text-void-400">{blurb}</p>

      <span className={`mt-2 text-sm font-medium ${accentText}`}>
        {cta}
        <span className="ml-1 inline-block transition-transform group-hover:translate-x-1">→</span>
      </span>
    </Link>
  );
}

export function Landing() {
  return (
    <div className="flex flex-col gap-12">
      <div className="max-w-2xl">
        <h1 className="font-display text-4xl font-semibold text-white">Personnel Records</h1>
        <p className="mt-4 text-base leading-relaxed text-void-400">
          Build a service record for the Aetherstream fleet. Choose your branch — the heavy hulls of
          Leviathan, or the fast wings of Interceptor.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <PathCard
          to="/captains"
          eyebrow="Leviathan"
          title="Captains"
          blurb="Command a capital ship. Build a captain from the Leviathan service rules and export the finished record."
          accent="leviathan"
          cta="Begin"
        />
        <PathCard
          to="/pilots"
          eyebrow="Interceptor"
          title="Pilots"
          blurb="Fly the fast wings. Interceptor pilot creation opens once the Leviathan module is complete."
          accent="interceptor"
          cta="See status"
          status="In development"
        />
      </div>
    </div>
  );
}
